import { AcimaLauncher } from './launcher'

import bagShopping from './icons/bag-shopping.svg';
import handHoldingDollar from './icons/hand-holding-dollar.svg';
import signPosts from './icons/signs-post.svg';

let darkBlue = '#050044'
let orange = '#f75200'
let darkGray = '#696871'
let font = '\'Nunito Sans\', sans-serif'

export class Modal {
  private _parentElem: HTMLElement
  private _modalElement: HTMLDivElement | null = null
  private _buttonDiv: HTMLElement | null = null
  private _merchantId: string
  private _iframeUrl: string
  private enableCTA: boolean
  private zIndex: string = '1000'

  /**
   * Creates the acima modal.
   * 
   * @param buttonId The element to add the button to.
   * @param merchantId The merchant ID.
   * @param iframeUrl The URL of the iframe.
   * @param enableCTA Enables the CTA button in the modal. Defaults to true. 
   * @param extraConfig Extra, optional configuration options for the modal.
   */
  constructor(buttonId: string, merchantId: string, iframeUrl: string, enableCTA: boolean, extraConfig: ModalExtraConfig = {}) {
    this._merchantId = merchantId
    this._iframeUrl = iframeUrl
    this.enableCTA = enableCTA

    // Extract extra config
    this.zIndex = extraConfig?.zIndex || this.zIndex

    this._parentElem = document.body
    this._buttonDiv = document.getElementById(buttonId)

    let font1 = document.createElement('link')
    font1.setAttribute('rel', 'preconnect')
    font1.setAttribute('href', 'https://fonts.googleapis.com')


    let font2 = document.createElement('link')
    font2.setAttribute('rel', 'preconnect')
    font2.setAttribute('href', 'https://fonts.gstatic.com')
    font2.setAttribute('crossorigin', '')

    let font3 = document.createElement('link')
    font3.setAttribute('rel', 'stylesheet')
    font3.setAttribute('href', 'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;800&display=swap')

    let hd = document.getElementsByTagName('head')[0]
    hd?.appendChild(font1)
    hd?.appendChild(font2)
    hd?.appendChild(font3)


    let button = document.createElement('img')
    button.src = `${iframeUrl}/assets/learnMore.svg`
    button.addEventListener('click', this.showModal)

    this._buttonDiv?.appendChild(button)
  }

  createModal() {
    this._modalElement = document.createElement('div')
    this._modalElement.setAttribute('allowtransparency', 'true')
    this._modalElement.style.zIndex = this.zIndex
    this._modalElement.style.width = '100%'
    this._modalElement.style.height = '100%'
    this._modalElement.style.border = 'none'
    this._modalElement.style.position = 'fixed'
    this._modalElement.style.top = '0'
    this._modalElement.style.left = '0'
    this._modalElement.style.backgroundColor = 'rgba(70, 68, 68, 0.5)'
    this._modalElement.style.display = 'flex'
    this._modalElement.style.boxSizing = 'border-box'
    this._modalElement.style.flexDirection = 'column'
    const modalClassName = 'acima-marketing-modal-responsive-padding'
    this._modalElement.className = modalClassName

    const modalStyle = document.createElement('style');
    modalStyle.innerHTML = `
.${modalClassName} { padding: 80px; }

#explanation-container {
  display: flex;
}

@media (max-width: 900px) {
    .${modalClassName} { padding: 2.5rem; }
}

@media (max-width: 768px) {
  .${modalClassName} { padding: 1rem; }

  #explanation-container {
    display: block;
  }

  #explanation-container > * {
    margin: 1em 0;
  }
}
`

    this._modalElement.appendChild(this.modalContent())
    this._modalElement.appendChild(modalStyle)

    this._parentElem.appendChild(this._modalElement)
  }

  showModal = () => {
    if (this._modalElement) {
      this._modalElement.style.display = 'flex'
    }
    else {
      this.createModal()
    }
  }

  hideModal() {
    if (this._modalElement) {
      this._modalElement.style.display = 'none'
    }
    else {
      console.error('Modal already closed')
    }
  }

  modalContent(): HTMLElement {
    let rec = document.createElement('div')
    rec.style.maxWidth = '850px'
    rec.style.maxHeight = 'auto'
    rec.style.alignSelf = 'center'
    rec.style.borderRadius = '10px'
    rec.style.margin = 'auto'
    rec.style.backgroundColor = 'white'
    rec.style.padding = '20px'
    rec.style.fontFamily = font
    rec.style.display = 'flex'
    rec.style.flexDirection = 'column'
    rec.style.overflow = 'hidden'

    rec.appendChild(this.closeButton())

    let scrollable = document.createElement('div')
    scrollable.style.overflow = 'auto';
    scrollable.style.marginTop = '0.5rem';
    scrollable.appendChild(this.acimaLogo())
    scrollable.appendChild(this.headline())
    scrollable.appendChild(this.byline())
    scrollable.appendChild(this.leasingExplanation())
    if (this.enableCTA) {
      scrollable.appendChild(this.callToAction())
    }
    scrollable.appendChild(this.caveat())
    rec.appendChild(scrollable)

    return rec
  }

  closeButton(): HTMLElement {
    const closeButton = document.createElement('div')
    closeButton.style.fontSize = '1.5rem'
    closeButton.style.fontWeight = '700'
    closeButton.style.lineHeight = '1'
    closeButton.style.height = '24px'

    let closeX = document.createElement('span')
    closeX.textContent = 'X'
    closeX.style.float = 'right'
    closeX.addEventListener('click', () => this.hideModal())

    closeButton.appendChild(closeX)
    return closeButton
  }

  acimaLogo(): HTMLElement {
    const logoContainer = document.createElement('div')
    let logoImage = document.createElement('img')
    logoImage.src = `${this._iframeUrl}/assets/acimaLeasingLogo.png`
    logoImage.style.display = 'block'
    logoImage.style.margin = 'auto'
    logoContainer.appendChild(logoImage)
    return logoContainer
  }

  headline(): HTMLElement {
    let headlineContent = document.createElement('div')
    headlineContent.textContent = 'Get the stuff you need - all without perfect credit.*'
    headlineContent.style.fontSize = '45px'
    headlineContent.style.fontWeight = '800'
    headlineContent.style.textAlign = 'center'
    headlineContent.style.margin = '40px auto'
    headlineContent.style.maxWidth = '600px'
    headlineContent.style.lineHeight = '1'
    headlineContent.style.color = darkBlue
    return headlineContent
  }

  byline(): HTMLElement {
    const bylineContent = document.createElement('div')
    bylineContent.textContent = 'Our flexible, lease-to-own solution can provide you with instant shopping power.'
    bylineContent.style.textAlign = 'center'
    bylineContent.style.color = darkGray
    bylineContent.style.fontSize = '20px'
    bylineContent.style.marginTop = '40px'

    return bylineContent
  }

  leasingExplanation(): HTMLElement {
    const happyApplicantImg = document.createElement('img')
    happyApplicantImg.setAttribute('id', 'happy-applicant-img')
    happyApplicantImg.src = `${this._iframeUrl}/assets/happyCustomer.jpg`
    if (this.smallDevice() == true) {
      happyApplicantImg.style.margin = 'auto'
    }

    const cp1 = this.labelWithIcon(bagShopping, 'Lease $300 to $5,000** worth of eligible merchandise, such as furniture, electronics, and more.')
    const cp2 = this.labelWithIcon(signPosts, 'Just meet some basic income requirements and get an instant approval decision.')
    const cp3 = this.labelWithIcon(handHoldingDollar, 'Make payments until you own it or purchase early at a discount—either way it\'s yours to keep.†')
    const checkPoints = document.createElement('div')
    checkPoints.setAttribute('id', 'check-points')
    checkPoints.style.display = 'flex'
    checkPoints.style.flexDirection = 'column'
    checkPoints.style.justifyContent = 'space-between'
    if (this.smallDevice() == true) {
      checkPoints.style.width = 'fit-content'
      checkPoints.style.marginTop = '30px'
    } else {
      checkPoints.style.width = '320px'
    }

    checkPoints.appendChild(cp1)
    checkPoints.appendChild(cp2)
    checkPoints.appendChild(cp3)

    const explanationContainer = document.createElement('div')
    explanationContainer.setAttribute('id', 'explanation-container')
    explanationContainer.style.justifyContent = 'space-between'
    explanationContainer.style.width = '85%'
    explanationContainer.style.margin = '40px auto'
    explanationContainer.appendChild(happyApplicantImg)
    explanationContainer.appendChild(checkPoints)
    if (this.smallDevice() == true) {
      explanationContainer.style.flexDirection = 'column'
    } else {
      explanationContainer.style.flexDirection = 'row'
    }

    return explanationContainer
  }


  labelWithIcon(icon: string, str: string): HTMLElement {
    let cp = document.createElement('div')
    cp.style.display = 'flex'
    cp.style.flexDirection = 'row'

    let ckIconContainer = document.createElement('div')
    ckIconContainer.style.backgroundImage = `url(${icon})`;
    ckIconContainer.style.backgroundRepeat = 'no-repeat'
    ckIconContainer.style.backgroundPosition = 'center'
    ckIconContainer.style.fontSize = '25px';
    ckIconContainer.style.margin = '-3 20px 0 0'
    ckIconContainer.style.width = '35px'
    ckIconContainer.style.height = '35px'


    const sp = document.createElement('div')
    sp.textContent = str
    sp.style.color = darkGray
    sp.style.flexBasis = "0"
    sp.style.flexGrow = "1"

    cp.appendChild(ckIconContainer)
    cp.appendChild(sp)

    return cp
  }


  callToAction(): HTMLElement {
    const applyButton = document.createElement('div')
    applyButton.textContent = 'Apply Now'
    applyButton.style.borderRadius = '30px'
    applyButton.style.textAlign = 'center'
    applyButton.style.backgroundColor = orange
    applyButton.style.color = 'fff'
    applyButton.style.fontWeight = '600'
    applyButton.style.lineHeight = '32px'
    applyButton.style.cursor = 'pointer'
    applyButton.style.margin = 'auto'
    applyButton.style.padding = '0.25em 1.75em'
    applyButton.addEventListener('click', () => { this.hideModal(); this.preapproval() })

    const applyBlock = document.createElement('div')
    applyBlock.style.display = 'flex'
    applyBlock.style.flexDirection = 'column'
    applyBlock.style.justifyContent = 'center'
    applyBlock.style.width = '100%'
    applyBlock.appendChild(applyButton)

    const logoImg = document.createElement('img')
    logoImg.src = `${this._iframeUrl}/assets/acimaAlone.png`
    logoImg.style.margin = 'auto'
    logoImg.style.display = 'block'

    const callToActionContainer = document.createElement('div')
    callToActionContainer.setAttribute('id', 'call-to-action')
    callToActionContainer.style.display = 'flex'
    callToActionContainer.style.justifyContent = 'space-between'
    callToActionContainer.style.maxWidth = '400px'
    callToActionContainer.style.margin = '0 auto'
    if (this.smallDevice() == true) {
      callToActionContainer.style.flexDirection = 'column'
    } else {
      callToActionContainer.style.flexDirection = 'row'
    }

    callToActionContainer.appendChild(applyBlock)

    return callToActionContainer
  }


  caveat(): HTMLElement {
    const finePrintContainer = document.createElement('div')
    finePrintContainer.textContent = '*Acima offers lease-to-own transactions. This is not an offer for a loan, credit, or financing. **Approval amount based on cash price Acima will pay to acquire the product(s) for your lease. Ownership through leasing costs more than the cash price. †Action required to exercise. See agreement for details.'
    finePrintContainer.style.color = darkGray
    finePrintContainer.style.fontSize = '13px'
    finePrintContainer.style.width = '80%'
    finePrintContainer.style.margin = '40px auto'
    finePrintContainer.style.textAlign = 'center'
    return finePrintContainer
  }

  preapproval() {
    const acima = new AcimaLauncher({
      merchantId: this._merchantId,
      iframeUrl: this._iframeUrl
    })
    return acima.preapproval({})
  }

  smallDevice(): Boolean {
    return window.innerWidth <= 550
  }
}
